import clsx from 'clsx'
import type {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  MouseEventHandler,
} from 'react'
import React, { createContext, useState, useContext } from 'react'
import type { TabColor, TabSize } from './Tab'

interface GroupProps {
  /**
   * The alignment of tabs within the group
   * @default 'stretch'
   */
  align?: TabAlign
  /**
   * The orientation of the icon adornment relative to the tab content
   * @default 'vertical'
   */
  adornmentPosition?: Orientation
  /**
   * The color scheme of the tabs
   * @default 'default'
   */
  color?: TabColor
  /** The value of the tab that should be selected by default */
  defaultTab: string
  /**
   * If true, all tabs in the group are disabled
   */
  disabled?: boolean
  /**
   * Callback fired when any tab in the group is activated
   */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /**
   * The orientation of the tab group
   * @default 'horizontal'
   */
  orientation?: Orientation
  /**
   * The size of the tabs in the group
   * @default 'large'
   */
  size?: TabSize
}

export const TAB_ALIGN_VALUES = ['left', 'stretch', 'right']
export type TabAlign = typeof TAB_ALIGN_VALUES[number]

type GroupState = {
  align: TabAlign
  color: TabColor
  disabled?: boolean | undefined
  onClick?: MouseEventHandler<HTMLButtonElement>
  selectedTab: string
  setSelectedTab: Dispatch<SetStateAction<string>>
  size: TabSize
  adornmentPosition: Orientation
}

export type Orientation = 'vertical' | 'horizontal'

export const GroupContext = createContext<GroupState | null>(null)

export const useGroup = (): GroupState => useContext(GroupContext) as GroupState

function Group({
  align = 'stretch',
  children,
  color = 'default',
  defaultTab,
  disabled,
  onClick,
  orientation = 'horizontal',
  size = 'large',
  adornmentPosition = 'vertical',
}: PropsWithChildren<GroupProps>): JSX.Element {
  const [selectedTab, setSelectedTab] = useState(defaultTab)

  return (
    <div
      className={clsx(
        'flex w-full flex-wrap border-b border-solid border-black-10-hex',
        orientation === 'vertical' ? 'flex-col' : null,
        align === 'left' && orientation === 'horizontal'
          ? 'justify-start'
          : null,
        align === 'right' && orientation === 'horizontal'
          ? 'justify-end'
          : null,
      )}
      role="tablist"
    >
      <GroupContext.Provider
        value={{
          align,
          color,
          disabled,
          onClick,
          selectedTab,
          setSelectedTab,
          size,
          adornmentPosition,
        }}
      >
        {children}
      </GroupContext.Provider>
    </div>
  )
}

export default Group
